import InfoPanel from "components/general/InfoPanel";
import PaperDashboardCard from "components/general/PaperDashboardCard";
import { Box, Button, HStack, Typography, VStack } from "components/ui";
import { LinearGradient } from "expo-linear-gradient";
import { Filter, Focus } from "lucide-react-native";
import React from "react";
import { Easing, ViewStyle } from "react-native";
import Animated, { interpolate, useAnimatedStyle, useSharedValue, withRepeat, withTiming } from "react-native-reanimated";
import { useIsMobile } from "utils/hooks/useIsMobile";

interface SnapshotSectionProps {
	productPanelList: any;
	productsIsLoading: boolean;
	products: any;
	ordersIsLoading: boolean;
	sites: any;
	orderPanelList: any;
}

const SkeletonLoader = () => {
	const isMobile = useIsMobile();
	const shimmerValue = useSharedValue(0);
	const pulseValue = useSharedValue(0);

	React.useEffect(() => {
		shimmerValue.value = withRepeat(withTiming(1, { duration: 1800, easing: Easing.inOut(Easing.ease) }), -1, true);
		pulseValue.value = withRepeat(withTiming(1, { duration: 1200, easing: Easing.inOut(Easing.ease) }), -1, true);
	}, []);

	const gradientStyle = useAnimatedStyle(() => ({
		position: "absolute",
		top: 0,
		bottom: 0,
		left: 0,
		right: 0,
		transform: [{ translateX: interpolate(shimmerValue.value, [0, 1], [-300, 300]) }],
	}));

	const pulseStyle = useAnimatedStyle(() => ({
		opacity: 0.3 + pulseValue.value * 0.4,
		backgroundColor: "#e0e0e0",
	}));

	interface ShimmerBoxProps {
		width: ViewStyle["width"];
		height: ViewStyle["height"];
		borderRadius?: number;
		marginBottom?: number;
	}

	const ShimmerBox = ({ width, height, borderRadius = 8, marginBottom }: ShimmerBoxProps) => (
		<Box height={height} width={width} borderRadius={borderRadius} overflow="hidden" mb={marginBottom}>
			<Animated.View style={[pulseStyle, { position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }]} />
			<Animated.View style={gradientStyle}>
				<LinearGradient colors={["rgba(255,255,255,0)", "rgba(255,255,255,0.8)", "rgba(255,255,255,0)"]} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }} style={{ width: 300, height: "100%" }} />
			</Animated.View>
		</Box>
	);

	const CardSkeleton = () => (
		<Box bg="$basicwhite" borderRadius={12} padding={28} overflow="hidden">
			<ShimmerBox width={120} height={32} marginBottom={16} />
			<ShimmerBox width="80%" height={60} marginBottom={24} />
			<Box gap={12}>
				{[1, 2, 3].map((_, index) => (
					<ShimmerBox key={index} width="100%" height={80} />
				))}
			</Box>
		</Box>
	);

	return (
		<Box>
			<Box padding={isMobile ? 16 : 28}>
				<Box alignItems="center" flexDirection="row">
					<Typography.H2 sx={{ marginRight: "auto" }}>Snapshot</Typography.H2>
					<Button py={12} px={12} borderRadius={12} gap={6} backgroundColor="$primarylight" variant="primary">
						<Filter color={"white"} />
					</Button>
				</Box>
				<Box py={28} flexDirection="row" gap={28}>
					<Box flex={1}>
						<CardSkeleton />
					</Box>
					<Box flex={1}>
						<CardSkeleton />
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

const SnapshotSection = ({ productPanelList, productsIsLoading, products, ordersIsLoading, sites, orderPanelList }: SnapshotSectionProps) => {
	const isMobile = useIsMobile();

	if (productsIsLoading || ordersIsLoading) {
		return <SkeletonLoader />;
	}

	return (
		<Box>
			<Box padding={isMobile ? 16 : 28}>
				<Box alignItems="center" flexDirection="row">
					<HStack gap={24} alignItems="center">
						<Focus size={36} />
						<Typography.H2>Snapshot</Typography.H2>
					</HStack>
					<Button sx={{ marginLeft: "auto" }} py={12} px={12} borderRadius={12} gap={6} backgroundColor="$primarylight" variant="primary">
						<Filter color={"white"} />
					</Button>
				</Box>
				<Box py={28} flexDirection={isMobile ? "column" : "row"} gap={28}>
					<Box flex={isMobile ? "initial" : 1}>
						<PaperDashboardCard
							title="Products"
							description={`In the last 12 months you purchased ${products?.uniqueItemsOrdered} different lines of products. ${products?.enviroplusItemsOrdered} of these were Enviroplus products.`}
							buttonLabel="Products with price changes"
							buttonAction={() => {}}
							hideButton
							flex={1}
							component={
								<Box gap={12}>
									{productPanelList.map((panel: any, index: number) => (
										<InfoPanel key={index} {...panel} />
									))}
								</Box>
							}
						/>
					</Box>
					<Box flex={isMobile ? "initial" : 1}>
						<PaperDashboardCard
							title="Orders"
							description={`You have ${sites?.withActivity} active sites in the last 12 months.`}
							buttonLabel="All orders"
							buttonAction={() => {}}
							flex={1}
							hideButton
							component={
								<Box gap={12}>
									{orderPanelList.map((panel: any, index: number) => (
										<InfoPanel key={index} {...panel} />
									))}
								</Box>
							}
						/>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default SnapshotSection;
